import React, { useState } from 'react';
import { getPillar } from '../pillars';

/**
 * GoldenLearnDashboard - Level Browser and Progress Overview
 *
 * Reference UI: Derivco Facilities Dashboard
 * Colors: Deep blue (#003366), teal (#008080), lime stats (#a3e635)
 */
function GoldenLearnDashboard({
  levels,
  completed,
  currentLevel,
  onSelectLevel,
  onStartLearning,
  onBackToSIFISO,
  onEnterPracticeMode,
  onEnterLearningCircle
}) {
  const [selectedPillar, setSelectedPillar] = useState('all');

  const totalLevels = levels.length;
  const completedCount = completed.filter(c => c).length;
  const inProgressCount = currentLevel > 0 && !completed[currentLevel] ? 1 : 0;
  const notStartedCount = totalLevels - completedCount - inProgressCount;
  const readinessPercent = Math.round((completedCount / totalLevels) * 100);

  const pillarCounts = {
    all: totalLevels,
    digital: levels.filter(l => l.pillar?.primary === 'digital').length,
    ai: levels.filter(l => l.pillar?.primary === 'ai').length,
    economic: levels.filter(l => l.pillar?.primary === 'economic').length,
    'problem-solving': levels.filter(l => l.pillar?.primary === 'problem-solving').length,
  };

  const filteredLevels = selectedPillar === 'all'
    ? levels
    : levels.filter(l => l.pillar?.primary === selectedPillar);

  const getStatusText = (index) => {
    if (completed[index]) return 'Complete';
    if (index === 0) return 'Ready';
    if (completed[index - 1]) return 'Ready';
    return 'Locked';
  };

  const canStartLevel = (index) => {
    return index === 0 || completed[index - 1];
  };

  return (
    <div className="min-h-screen text-white" style={{ background: '#1a202c' }}>
      {/* Header */}
      <div
        className="relative min-h-[150px] flex items-center justify-center"
        style={{
          background: 'linear-gradient(135deg, #1a365d 0%, #2d3748 50%, #1a202c 100%)',
          borderBottom: '1px solid #475569'
        }}
      >
        <div className="max-w-7xl mx-auto px-6 py-6 w-full">
          {/* Back Navigation */}
          <div className="mb-4">
            <button
              onClick={onBackToSIFISO}
              className="px-4 py-2 rounded text-xs font-semibold transition text-white"
              style={{ background: '#008080' }}
            >
              ← Back to SIFISO
            </button>
          </div>

          {/* Header Content */}
          <div className="text-center">
            <h1 className="text-3xl font-bold mb-2">
              <span className="text-white">SIFISO</span>{' '}
              <span className="text-2xl font-semibold italic" style={{ color: '#FFD700' }}>Golden Learn</span>
              <span className="text-white"> — Python Fundamentals</span>
            </h1>
            <p className="text-sm font-semibold" style={{ color: '#00BFFF' }}>
              Your current readiness: {completedCount > 0 ? `${readinessPercent}%` : 'Not started'}
            </p>
          </div>
        </div>
      </div>

      {/* Dashboard Grid */}
      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mb-6">
          {/* Pillar Filter */}
          <div className="lg:col-span-3">
            <div
              className="rounded-lg p-4"
              style={{
                background: 'rgba(0, 51, 102, 0.7)',
                borderLeft: '4px solid #008080'
              }}
            >
              <h3 className="text-sm font-bold mb-3 text-white">PILLAR FILTER</h3>
              <div className="space-y-2">
                {[
                  { key: 'all', label: 'All', icon: '' },
                  { key: 'digital', label: 'Digital', icon: '💻' },
                  { key: 'ai', label: 'AI', icon: '🤖' },
                  { key: 'economic', label: 'Economic', icon: '💡' },
                  { key: 'problem-solving', label: 'Problem-Solving', icon: '🧩' }
                ].map(pillar => (
                  <button
                    key={pillar.key}
                    onClick={() => setSelectedPillar(pillar.key)}
                    className="w-full text-left px-3 py-2 rounded transition text-white text-xs"
                    style={{
                      background: selectedPillar === pillar.key ? '#008080' : '#475569'
                    }}
                  >
                    {pillar.icon} {pillar.label} ({pillarCounts[pillar.key]})
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Progress Stats */}
          <div className="lg:col-span-5">
            <div
              className="rounded-lg p-4"
              style={{
                background: 'rgba(0, 51, 102, 0.7)',
                borderLeft: '4px solid #008080'
              }}
            >
              <h3 className="text-sm font-bold mb-3 text-white">YOUR PROGRESS</h3>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="p-2 rounded text-center" style={{ background: '#475569' }}>
                  <div className="font-bold" style={{ color: '#a3e635' }}>{completedCount}/{totalLevels}</div>
                  <div style={{ color: '#d1d5db' }}>Completed</div>
                </div>
                <div className="p-2 rounded text-center" style={{ background: '#475569' }}>
                  <div className="font-bold" style={{ color: '#a3e635' }}>{inProgressCount}</div>
                  <div style={{ color: '#d1d5db' }}>In Progress</div>
                </div>
                <div className="p-2 rounded text-center" style={{ background: '#475569' }}>
                  <div className="font-bold" style={{ color: '#a3e635' }}>{notStartedCount}</div>
                  <div style={{ color: '#d1d5db' }}>Not Started</div>
                </div>
                <div className="p-2 rounded text-center" style={{ background: '#475569' }}>
                  <div className="font-bold" style={{ color: '#a3e635' }}>{readinessPercent}%</div>
                  <div style={{ color: '#d1d5db' }}>Readiness</div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="lg:col-span-4">
            <div
              className="rounded-lg p-4"
              style={{
                background: 'rgba(0, 51, 102, 0.7)',
                borderLeft: '4px solid #008080'
              }}
            >
              <h3 className="text-sm font-bold mb-3 text-white">QUICK ACTIONS</h3>
              <div className="space-y-2">
                <button
                  onClick={() => completedCount === 0 ? onStartLearning() : onSelectLevel(currentLevel)}
                  className="w-full px-4 py-2 rounded font-semibold transition text-white text-xs"
                  style={{ background: '#008080' }}
                >
                  {completedCount === 0 ? 'Start Level 1 →' : `Continue Level ${currentLevel + 1} →`}
                </button>
                <button
                  onClick={onEnterPracticeMode}
                  className="w-full px-4 py-2 rounded font-semibold transition text-white text-xs"
                  style={{ background: '#7c3aed' }}
                >
                  🎯 Practice Mode
                </button>
                <button
                  onClick={onEnterLearningCircle}
                  className="w-full px-4 py-2 rounded font-semibold transition text-white text-xs"
                  style={{ background: '#16a34a' }}
                >
                  🤝 Learning Circles
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Level Table */}
        <div
          className="rounded-lg overflow-hidden"
          style={{
            background: 'rgba(0, 51, 102, 0.7)',
            borderLeft: '4px solid #008080'
          }}
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead style={{ background: '#1a365d', borderBottom: '2px solid #008080' }}>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-white">#</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-white">Title</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-white">Pillar</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-white">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-white">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredLevels.map((level) => {
                  const levelIndex = levels.findIndex(l => l.id === level.id);
                  const pillar = getPillar(level.pillar?.primary || 'problem-solving');
                  const canStart = canStartLevel(levelIndex);

                  return (
                    <tr
                      key={level.id}
                      className="transition"
                      style={{
                        borderBottom: '1px solid #475569',
                        opacity: canStart ? 1 : 0.5
                      }}
                    >
                      <td className="px-4 py-3 font-mono text-xs" style={{ color: '#CCCCCC' }}>
                        {level.id}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-xs text-white">{level.title}</div>
                        <div className="text-xs mt-1" style={{ color: '#CCCCCC' }}>{level.description}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium"
                          style={{
                            background: '#475569',
                            color: '#00BFFF'
                          }}
                        >
                          <span>{pillar.icon}</span>
                          <span>{pillar.shortName}</span>
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className="text-xs font-medium"
                          style={{
                            color: completed[levelIndex] ? '#00FF99' : canStart ? '#00BFFF' : '#CCCCCC'
                          }}
                        >
                          {getStatusText(levelIndex)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {canStart ? (
                          <button
                            onClick={() => onSelectLevel(levelIndex)}
                            className="px-3 py-1 rounded text-xs font-semibold transition text-white"
                            style={{ background: '#008080' }}
                          >
                            {completed[levelIndex] ? 'Review' : 'Start'}
                          </button>
                        ) : (
                          <span className="text-xs" style={{ color: '#CCCCCC' }}>Locked</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GoldenLearnDashboard;
